import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Save-and-resume state for the rider and driver wizards. Persisted to localStorage under
 * `rides.wizard.v1`. NEVER put a document number, mobile number or any other sensitive
 * value in here — only ids, counts and the guardian's name/relationship/mobile that the
 * member typed for the current booking (cleared on reset).
 */

export type RidesRole = 'rider' | 'driver';
export type PartyMode = 'solo' | 'group';

export interface GuardianDraft {
  name: string;
  relationship: string;
  mobile: string;
}

export interface RiderSelection {
  eventId: number | null;
  collectionLocationId: number | null;
  dropOffLocationId: number | null;
  serviceTimeId: number | null;
  party: PartyMode | null;
  /** Solo rider's sex (RIDES_SEX) — drives maleCount/femaleCount. */
  soloSexId: number | null;
  maleCount: number;
  femaleCount: number;
  forMinor: boolean | null;
  guardian: GuardianDraft;
  chosenAvailabilityId: number | null;
}

export interface DriverDraft {
  eventIds: number[];
  collectionLocationId: number | null;
  dropOffLocationId: number | null;
  serviceTimeId: number | null;
  /** HH:mm */
  departureTime: string | null;
  seats: number;
  notes: string;
}

export interface RidesWizardState {
  role: RidesRole | null;
  rider: RiderSelection;
  driver: DriverDraft;
  updatedAt: number | null;
  setRole: (role: RidesRole | null) => void;
  setRider: (patch: Partial<RiderSelection>) => void;
  setDriver: (patch: Partial<DriverDraft>) => void;
  resetRider: () => void;
  resetDriver: () => void;
  reset: () => void;
}

export const EMPTY_RIDER: RiderSelection = {
  eventId: null,
  collectionLocationId: null,
  dropOffLocationId: null,
  serviceTimeId: null,
  party: null,
  soloSexId: null,
  maleCount: 0,
  femaleCount: 0,
  forMinor: null,
  guardian: { name: '', relationship: '', mobile: '' },
  chosenAvailabilityId: null,
};

export const EMPTY_DRIVER: DriverDraft = {
  eventIds: [],
  collectionLocationId: null,
  dropOffLocationId: null,
  serviceTimeId: null,
  departureTime: null,
  seats: 3,
  notes: '',
};

export const useRidesWizard = create<RidesWizardState>()(
  persist(
    (set) => ({
      role: null,
      rider: EMPTY_RIDER,
      driver: EMPTY_DRIVER,
      updatedAt: null,
      setRole: (role) => set({ role, updatedAt: Date.now() }),
      setRider: (patch) => set((s) => ({ rider: { ...s.rider, ...patch }, role: 'rider', updatedAt: Date.now() })),
      setDriver: (patch) => set((s) => ({ driver: { ...s.driver, ...patch }, role: 'driver', updatedAt: Date.now() })),
      resetRider: () => set((s) => ({ rider: EMPTY_RIDER, role: s.role === 'rider' ? null : s.role, updatedAt: Date.now() })),
      resetDriver: () => set((s) => ({ driver: EMPTY_DRIVER, role: s.role === 'driver' ? null : s.role, updatedAt: Date.now() })),
      reset: () => set({ role: null, rider: EMPTY_RIDER, driver: EMPTY_DRIVER, updatedAt: null }),
    }),
    { name: 'rides.wizard.v1', version: 1 },
  ),
);

/** Party size derived from the composition. */
export function partySize(r: RiderSelection): number {
  return Math.max(0, r.maleCount) + Math.max(0, r.femaleCount);
}

/** The earliest rider step whose prerequisites are missing (null = everything up to matches is set). */
export function riderMissingStep(r: RiderSelection, upTo: 'route' | 'party' | 'matches' | 'verify'): string | null {
  if (r.eventId == null) return '/ride/event';
  if (upTo === 'route') return null;
  if (r.collectionLocationId == null || r.dropOffLocationId == null || r.collectionLocationId === r.dropOffLocationId) return '/ride/route';
  if (upTo === 'party') return null;
  if (!r.party || partySize(r) < 1 || (r.party === 'group' && partySize(r) < 2)) return '/ride/party';
  if (upTo === 'matches') return null;
  if (r.chosenAvailabilityId == null) return '/ride/matches';
  return null;
}

/** The earliest driver step whose prerequisites are missing. */
export function driverMissingStep(d: DriverDraft, upTo: 'route' | 'summary'): string | null {
  if (!d.eventIds.length) return '/drive/events';
  if (upTo === 'route') return null;
  if (d.collectionLocationId == null || d.dropOffLocationId == null || d.collectionLocationId === d.dropOffLocationId) return '/drive/route';
  if (d.seats < 1) return '/drive/route';
  return null;
}

/** True when there is a half-finished flow worth resuming. */
export function hasRiderDraft(r: RiderSelection): boolean {
  return r.eventId != null;
}
export function hasDriverDraft(d: DriverDraft): boolean {
  return d.eventIds.length > 0;
}
